import axios, { AxiosRequestConfig } from 'axios';
import useAuthStore from '@store/auth';
import { objectToUrlParamStr } from '@utils/util';
import generateSignature from '@utils/signature';
import { getRandomStr } from '@/utils/util';
import { storeToRefs } from 'pinia';
import { codeJson } from '@/utils/locales';

import usePopMessageStore from '@store/popMessage';
import useSubscriptionStore from '@store/subscription';
import { normalizeLocale } from '@/locales/resources';

axios.defaults.withCredentials = true;

const commonParams = {
  app: 'vgen',
  fr: 'web',
  lan: normalizeLocale(),
};
const signatureKey = 'X@S_i_g_n_a_t_u_r_e'.replace(/@/g, '-').replace(/_/g, '');
const commonParamsStr = objectToUrlParamStr(commonParams);
const host = import.meta.env.VUE_API_HOST_WHITELIST?.includes(location.hostname)
  ? '/'
  : `//${import.meta.env.VUE_API_HOST}`;

interface RequestConfig extends AxiosRequestConfig {
  startTime: number;
}

const service = axios.create({
  baseURL: `${host}`,
  timeout: 60 * 1000,
  withCredentials: true,
});

service.interceptors.request.use(async (config) => {
  if (!config?.url?.includes('?')) {
    config.url += `?${commonParamsStr}`;
  } else {
    config.url += `&${commonParamsStr}`;
  }

  if (config?.params || config?.data) {
    const key = getRandomStr(5);
    const hash = await generateSignature({
      ...(config.method === 'get'
        ? { ...config.params, ...commonParams }
        : config.data),
    });

    if (hash) {
      config.headers[signatureKey] = hash + key;
    }
  }

  const requestConfig: Partial<RequestConfig> = config;

  return requestConfig;
});

service.interceptors.response.use(
  (response) => {
    const { data } = response;

    // 不包含弹窗提示
    if (
      data?.status > 1000 &&
      (data?.status !== 30702 || data?.status !== 30802)
    ) {
      // 弹窗提示

      if (!codeJson?.[data?.status]?.btn) {
        const popMessageStore = usePopMessageStore();
        popMessageStore.show({
          type: 'error',
          message: `code.${data?.status}.text`,
        });
      } else {
        const subscriptionStore = useSubscriptionStore();
        const { subscriptionCode, limitLen, leftLen, upgradeSubGroupIndex } =
          storeToRefs(subscriptionStore);

        // 错误状态码
        subscriptionCode.value = data.status;
        // 使用次数限制
        limitLen.value = data?.data?.limit;
        // 剩余次数
        leftLen.value = data?.data?.left;
        // 产品等级
        upgradeSubGroupIndex.value = data?.data?.upgrade_sub_group_index;
      }
    }
    return data;
  },
  (error) => {
    const authStore = useAuthStore();
    const { response } = error;
    const { status } = response;

    // 授权登录, 避免死循环跳转
    if (status === 400) {
      if (!authStore.redirectUrl) {
        authStore.skipPageOrShowLoginPop(location.href);
      }
    }

    return Promise.reject(error);
  }
);

export default service;
